import React, { useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { CheckCircle, ExternalLink, LifeBuoy, RotateCcw, XCircle } from "react-feather";
import { Link } from "react-router";
import { apiErrorMessage, useGet, usePost } from "../../api";
import { useAuth } from "../../hooks";
import {
  failureReasonBadge,
  failureReasonLabel,
  HELP_KIND_BRAND_STATUS,
  HELP_KIND_META,
  HelpKind,
  ITemplatePipelineItem,
  ITemplatePipelineListResponse,
  IWorkPipelineItem,
  IWorkPipelineTemplateHelp,
  IWorkPipelineTemplateHelpAction,
  SystemGroup,
  TEMPLATE_PIPELINE_OPEN_STAGES,
  TEMPLATE_PIPELINE_STAGE_META,
  TemplatePipelineEventType,
  TemplatePipelineStage,
  WORK_PIPELINE_BRAND_STATUS_LABELS,
  WorkPipelineBrandStatus,
} from "../../interfaces";
import {
  EVENT_LABEL,
  PROGRESS,
  since,
  stopped,
  TEMPLATE_HELP_ACTIONS,
  TTemplateHelpAction,
} from "./template-help-meta";

const RECENT_EVENTS = 6;

/** In-flight states read as a status on the row, not as a reason it is stuck. */
const REQUEST_STATUS: Partial<Record<TemplatePipelineStage, string>> = {
  [TemplatePipelineStage.Verifying]: "Retest running",
  [TemplatePipelineStage.NeedsTriage]: "Awaiting triage",
};

const helpLabel = (h: Pick<IWorkPipelineTemplateHelp, "helpKind" | "stage">) =>
  REQUEST_STATUS[h.stage] ??
  (h.helpKind
    ? HELP_KIND_META[h.helpKind as HelpKind].label
    : TEMPLATE_PIPELINE_STAGE_META[h.stage].label);

/** The agent's open request on a board card, with the last failure on hover. */
export const TemplateHelpBadge: React.FC<{
  help: IWorkPipelineTemplateHelp | null;
}> = ({ help }) => {
  if (!help) return null;
  const status = REQUEST_STATUS[help.stage];
  return (
    <Badge
      bg={status ? "info" : "warning"}
      text={status ? undefined : "dark"}
      title={[
        help.stage === TemplatePipelineStage.Verifying
          ? "Help was given and the agent's retest is queued or running. It resolves itself if that run passes."
          : help.stage === TemplatePipelineStage.NeedsTriage
            ? "Failed before runs recorded why. A retest sorts it into a stuck reason."
            : "The agent's walk keeps failing here and needs a person. Open the row to act.",
        help.errorMessage,
        stopped(help.failureStage),
        `${help.failureCount} failure${help.failureCount === 1 ? "" : "s"}`,
      ]
        .filter(Boolean)
        .join("\n")}
    >
      <LifeBuoy size={11} className="me-1" />
      Agent: {helpLabel(help)}
    </Badge>
  );
};

/** The agent's request for this domain + geo, acted on under the board's claim. */
export const TemplateHelpPanel: React.FC<{
  item: IWorkPipelineItem;
  claimedByMe: boolean;
  onChanged: () => void;
}> = ({ item, claimedByMe, onChanged }) => {
  const { permissions, isAdmin } = useAuth();
  const groups = permissions?.groups ?? [];
  // Same lead set the API lets act on someone else's claim.
  const isLead =
    isAdmin ||
    groups.includes(SystemGroup.LeadQaTesters) ||
    groups.includes(SystemGroup.QaManagers);
  const { data, refetch } = useGet<{ item: ITemplatePipelineItem | null }>(
    `work-pipeline/${item._id}/template-help`
  );
  const post = usePost<ITemplatePipelineItem, IWorkPipelineTemplateHelpAction>();
  const [action, setAction] = useState<TTemplateHelpAction | null>(null);
  const [text, setText] = useState("");
  const [brandStatus, setBrandStatus] = useState<WorkPipelineBrandStatus | "">("");
  const [error, setError] = useState<string | null>(null);

  const t = data?.item ?? null;
  if (!t) return null;

  const open = TEMPLATE_PIPELINE_OPEN_STAGES.includes(t.stage);
  const verifying = t.stage === TemplatePipelineStage.Verifying;
  const notSuitable = t.stage === TemplatePipelineStage.NotSuitable;
  const status = REQUEST_STATUS[t.stage];
  // Reopening a closed request is a lead's call; the rest follow the board's claim.
  const canAct = open ? claimedByMe || isLead : isLead;
  const help = t.helpKind ? HELP_KIND_META[t.helpKind as HelpKind] : null;
  const mapped = t.helpKind ? HELP_KIND_BRAND_STATUS[t.helpKind as HelpKind] : undefined;
  const reason = t.errorType ? failureReasonBadge(t.errorType) : null;

  const actions: TTemplateHelpAction[] = open
    ? [
        ...(notSuitable ? (["run-anyway"] as TTemplateHelpAction[]) : []),
        ...(!verifying && !notSuitable ? (["help"] as TTemplateHelpAction[]) : []),
        "retest",
        "dismiss",
      ]
    : ["reopen"];

  const start = (a: TTemplateHelpAction) => {
    setText("");
    setError(null);
    setBrandStatus(a === "dismiss" && mapped ? mapped : "");
    setAction(a);
  };

  const submit = async () => {
    if (!action) return;
    const cfg = TEMPLATE_HELP_ACTIONS[action];
    const body: IWorkPipelineTemplateHelpAction = text.trim()
      ? { [cfg.field]: text.trim() }
      : {};
    if (action === "dismiss" && brandStatus) body.brandStatus = brandStatus;
    setError(null);
    try {
      await post.execute(`work-pipeline/${item._id}/template-help/${action}`, body);
      setAction(null);
      await refetch();
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "That didn't go through."));
    }
  };

  const events = [...(t.events ?? [])].reverse().slice(0, RECENT_EVENTS);

  return (
    <Alert variant={status ? "info" : open ? "warning" : "light"} className="border py-2">
      <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
        <LifeBuoy size={14} />
        <strong className="small">
          {open
            ? `The agent needs help: ${help?.label ?? TEMPLATE_PIPELINE_STAGE_META[t.stage].label}`
            : t.stage === TemplatePipelineStage.Resolved
              ? `Agent request resolved ${since(t.resolvedAt)}`
              : `Agent request dismissed${t.dismissReason ? `: ${t.dismissReason}` : ""}`}
        </strong>
        {status && (
          <Badge bg="info">
            {status}
            {verifying ? ` · queued ${since(t.retestRequestedAt)}` : ""}
          </Badge>
        )}
        {t.lastProgress && (
          <Badge bg={PROGRESS[t.lastProgress].bg} text={PROGRESS[t.lastProgress].text}>
            {PROGRESS[t.lastProgress].label}
          </Badge>
        )}
        {t.helpRoute && (
          <Badge bg="info" text="dark" title="A route was taught in the Checkout Extension">
            Route taught · {t.helpRoute.byName ?? "tester"}
          </Badge>
        )}
        <Link
          to={`/merchants/${t.merchantId}/edit`}
          target="_blank"
          className="ms-auto small"
          title="The merchant the agent walks: selectors, product pages and cart settings"
        >
          Merchant <ExternalLink size={11} />
        </Link>
      </div>

      <div className="small text-muted mb-2">
        {!open
          ? "History only. It reopens by itself if a run fails for a new reason."
          : verifying
          ? "It resolves itself if the next run passes, or comes back here with the new failure."
          : (help?.description ?? TEMPLATE_PIPELINE_STAGE_META[t.stage].description)}{" "}
        {open &&
          !notSuitable &&
          "To show it the route, open the site with the Checkout Extension's Help tab; it reports back to this row while you hold the claim."}
      </div>

      <div className="small mb-2">
        {t.errorMessage ?? <span className="text-muted">No message recorded</span>}
        <div className="mt-1 d-flex flex-wrap gap-1 align-items-center">
          {reason && (
            <Badge bg={reason.bg} text={reason.text}>
              {failureReasonLabel(t.errorType)}
            </Badge>
          )}
          {t.failureStage && (
            <Badge bg="light" text="dark" className="border">
              {stopped(t.failureStage)}
            </Badge>
          )}
          {t.device && (
            <Badge bg="light" text="dark" className="border">
              {t.device}
            </Badge>
          )}
          <span className="text-muted">
            {since(t.lastFailedAt)} · {t.failureCount} failure
            {t.failureCount === 1 ? "" : "s"}
          </span>
          {t.lastTaskId && (
            <Link to={`/tasks/${t.lastTaskId}`} target="_blank" title="The run that last failed">
              Open the run <ExternalLink size={11} />
            </Link>
          )}
        </div>
      </div>

      {t.merchantCouponCount === 0 && open && (
        <div className="small text-warning mb-2">
          No live coupons: a retest walks with an invalid probe code, so it proves the coupon
          field, apply button and failure message, but not a working discount.
        </div>
      )}
      {mapped && open && (
        <div className="small text-muted mb-2">
          Same as the brand status “{WORK_PIPELINE_BRAND_STATUS_LABELS[mapped]}”. Dismissing it can
          set that status.
        </div>
      )}

      {events.length > 0 && (
        <div className="small mb-2" style={{ maxHeight: 150, overflowY: "auto" }}>
          {events.map((e, i) => {
            const good =
              e.type === TemplatePipelineEventType.Verified ||
              e.type === TemplatePipelineEventType.RecoveredOnItsOwn;
            const bad =
              e.type === TemplatePipelineEventType.StillFailing ||
              e.type === TemplatePipelineEventType.Failed;
            return (
              <div key={i} className="border-top pt-1 mt-1">
                {good ? (
                  <CheckCircle size={11} className="text-success me-1" />
                ) : bad ? (
                  <XCircle size={11} className="text-danger me-1" />
                ) : e.type === TemplatePipelineEventType.Reopened ? (
                  <RotateCcw size={11} className="text-muted me-1" />
                ) : null}
                <span className="fw-semibold">{EVENT_LABEL[e.type]}</span>
                <span className="text-muted">
                  {" "}
                  · {new Date(e.at).toLocaleString()}
                  {e.byName ? ` · ${e.byName}` : ""}
                </span>
                {e.note && <div style={{ whiteSpace: "pre-wrap" }}>{e.note}</div>}
              </div>
            );
          })}
        </div>
      )}

      {actions.length > 0 && (
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {actions.map((a) => (
            <Button
              key={a}
              size="sm"
              variant={a === "run-anyway" ? "primary" : TEMPLATE_HELP_ACTIONS[a].variant}
              disabled={!canAct || post.loading}
              title={
                canAct
                  ? TEMPLATE_HELP_ACTIONS[a].explainer
                  : open
                    ? "Claim this domain first: the board's claim is the one that counts."
                    : "Only a lead or manager can reopen the agent's request."
              }
              onClick={() => start(a)}
            >
              {TEMPLATE_HELP_ACTIONS[a].title}
            </Button>
          ))}
          {!canAct && open && (
            <small className="text-muted">Claim the domain to act on the agent's request.</small>
          )}
        </div>
      )}

      <Modal show={!!action} onHide={() => setAction(null)} centered>
        {action && (
          <>
            <Modal.Header closeButton>
              <Modal.Title className="h6">{TEMPLATE_HELP_ACTIONS[action].title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="small text-muted">{TEMPLATE_HELP_ACTIONS[action].explainer}</p>
              <Form.Group>
                <Form.Label className="small fw-semibold">
                  {TEMPLATE_HELP_ACTIONS[action].fieldLabel}
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              </Form.Group>
              {action === "dismiss" && (
                <Form.Group className="mt-3">
                  <Form.Label className="small fw-semibold">Also set brand status</Form.Label>
                  <Form.Select
                    size="sm"
                    value={brandStatus}
                    onChange={(e) =>
                      setBrandStatus(e.target.value as WorkPipelineBrandStatus | "")
                    }
                    title="Records why the brand can't be worked, on this row"
                  >
                    <option value="">Leave as it is</option>
                    {Object.values(WorkPipelineBrandStatus).map((s) => (
                      <option key={s} value={s}>
                        {WORK_PIPELINE_BRAND_STATUS_LABELS[s]}
                      </option>
                    ))}
                  </Form.Select>
                  {notSuitable && (
                    <Form.Text>
                      Dismissing confirms the classification. If it is a real shop, use Run anyway
                      instead.
                    </Form.Text>
                  )}
                </Form.Group>
              )}
              {error && <Alert variant="danger" className="small py-2 mt-2">{error}</Alert>}
            </Modal.Body>
            <Modal.Footer>
              <Button size="sm" variant="secondary" onClick={() => setAction(null)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant={action === "dismiss" ? "danger" : "primary"}
                disabled={
                  post.loading ||
                  (TEMPLATE_HELP_ACTIONS[action].required && text.trim().length < 3)
                }
                onClick={submit}
              >
                {TEMPLATE_HELP_ACTIONS[action].submit}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </Alert>
  );
};

/** Requests a person dismissed. Their rows have left the board, so a lead reopens them from here. */
export const DismissedAgentRequests: React.FC<{
  search: string;
  canReopen: boolean;
  onChanged: () => void;
}> = ({ search, canReopen, onChanged }) => {
  const [page, setPage] = useState(1);
  const params = new URLSearchParams({ page: String(page), limit: "20" });
  if (search) params.set("search", search);
  const { data, loading, refetch } = useGet<ITemplatePipelineListResponse>(
    `work-pipeline/template-help/dismissed?${params.toString()}`
  );
  const reopenReq = usePost<ITemplatePipelineItem, { note?: string }>();
  const [error, setError] = useState<string | null>(null);
  const pages = Math.max(1, Math.ceil((data?.total ?? 0) / 20));

  const reopen = async (id: string) => {
    setError(null);
    try {
      await reopenReq.execute(`work-pipeline/template-help/${id}/reopen`, {});
      await refetch();
      onChanged();
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't reopen it."));
    }
  };

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <span className="text-muted small">
          {data ? `${data.total} agent request${data.total === 1 ? "" : "s"} dismissed` : ""}
        </span>
        <span className="text-muted small text-end" style={{ maxWidth: "60%" }}>
          Closed by a person. Each stays closed while runs fail for the same reason, and reopens by
          itself for a different one. Reopening puts it back in Template building.
        </span>
      </Card.Header>
      <Card.Body className="p-0">
        {error && (
          <Alert variant="danger" className="m-2 py-2 small" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {loading && !data ? (
          <div className="text-center p-4">
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        ) : !data?.items.length ? (
          <div className="text-center text-muted p-4">Nothing dismissed.</div>
        ) : (
          <div className="p-3 d-grid gap-2">
            {data.items.map((t) => (
              <div
                key={t._id}
                className="border rounded p-3 d-flex justify-content-between align-items-start gap-3"
              >
                <div style={{ minWidth: 0 }}>
                  <span className="fw-semibold">{t.merchantName || t.domain}</span>{" "}
                  <small className="text-muted">
                    {t.domain} · {t.countryCode}
                  </small>
                  <div className="small mt-1">
                    <Badge bg="secondary" className="me-1">
                      {t.dismissedHelpKind
                        ? HELP_KIND_META[t.dismissedHelpKind as HelpKind].label
                        : "Unclassified"}
                    </Badge>
                    {t.dismissReason ?? <span className="text-muted">No reason recorded</span>}
                  </div>
                  <small className="text-muted">
                    Dismissed {since(t.stageSince)}
                    {(() => {
                      const by = [...t.events]
                        .reverse()
                        .find((e) => e.type === TemplatePipelineEventType.Dismissed);
                      return by?.byName ? ` by ${by.byName}` : "";
                    })()}
                  </small>
                </div>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={!canReopen || reopenReq.loading}
                  title={
                    canReopen
                      ? TEMPLATE_HELP_ACTIONS.reopen.explainer
                      : "Only a lead or manager can reopen the agent's request."
                  }
                  onClick={() => reopen(t._id)}
                >
                  <RotateCcw size={12} className="me-1" /> Reopen
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
      {pages > 1 && (
        <Card.Footer className="d-flex justify-content-between align-items-center small">
          <span className="text-muted">
            Page {page} of {pages}
          </span>
          <div className="d-flex gap-1">
            <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        </Card.Footer>
      )}
    </Card>
  );
};
